import React from 'react'
import { clientActionTogglePlatformFeature, PlatformFeature } from 'api'
import { useDispatch, useSelector } from 'react-redux'
import { platformDetailSelector } from 'selectors/platformSelectors'
import { ToggleField } from 'view/components/toggle'
import { NotificationActions, NotificationType } from 'store/notification'
import { Translation } from 'locale'

interface PlatformToggleProps {
  'aria-labelledby'?: string
  'data-test-id'?: string
  label?: string
  platformFeature: PlatformFeature
  getConfirmationTitle: (newStatus: boolean) => string
  getConfirmationMessage: (newStatus: boolean) => string
  readOnly: boolean
}

export const PlatformToggle = ({
  'aria-labelledby': ariaLabelledBy,
  'data-test-id': dataTestId,
  label = '',
  platformFeature,
  getConfirmationTitle,
  getConfirmationMessage,
  readOnly
}: PlatformToggleProps) => {
  const dispatch = useDispatch()
  const platformDetail = useSelector(platformDetailSelector)
  const isFeatureEnabled = platformDetail && platformDetail.platform.enabledFeatures.includes(platformFeature)

  const onToggle = () => {
    if (!platformDetail) {
      throw new Error('Unable to locate current platform settings')
    }

    const isFeatureEnabled = platformDetail.platform.enabledFeatures.includes(platformFeature)

    const handler = () => {
      dispatch(
        clientActionTogglePlatformFeature({
          platformFeature,
          enabled: !isFeatureEnabled
        })
      )
    }

    dispatch(
      NotificationActions.showDialog({
        type: NotificationType.Confirm,
        titleText: getConfirmationTitle(!isFeatureEnabled),
        messageText: getConfirmationMessage(!isFeatureEnabled),
        buttons: [Translation.TC_BUTTON_YES, Translation.TC_BUTTON_NO],
        defaultButton: Translation.TC_BUTTON_YES,
        cancelButton: Translation.TC_BUTTON_NO,
        responseMap: {
          [Translation.TC_BUTTON_YES]: [handler]
        }
      })
    )
  }

  return (
    <ToggleField
      aria-labelledby={ariaLabelledBy}
      data-test-id={dataTestId}
      label={label}
      checked={!!isFeatureEnabled}
      onUpdate={onToggle}
      readOnly={readOnly}
    />
  )
}
